import React, { useEffect, useState } from 'react';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { UserModalContent } from './UserModalContent';
import { useTranslation } from 'react-i18next';
import { fetchUsersList, setInitialUserList, unblockUser } from '../../store/identyti/actions/users';
import { useIdentity } from '../../store/identyti/identity-context';
import { SimplePagination } from '../pagination/SimplePagination';
import clsx from 'clsx';
import { TablesNames } from '../../store/tables/tables-context';
import { Table } from '../table/Table';
import { IUserFilters, UserFilterSection } from './UserFilterSection';
import { PageTitle } from '../../layout/core';
import { Link } from 'react-router-dom';
import { IUserData } from '../../store/identyti/identity-interfaces';
import { USER_TABS } from '../../constants/users/userModalUtils';
import './UserList.scss';
import { resendInvitationToApplicant } from '../../store/identyti/actions/application';
import { faCircleCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonLoading } from '../ButtonLoading';
import { parseSearchParams } from '../../utils/utilities';
import { useLocation } from 'react-router';

export const UsersList = (): JSX.Element => {
	const {
		identityState: {
			usersList,
			isUserListLoading,
			usersListPaginationTokens,
			hasUserNextPage,
			isResendInvitationLoading
		},
		dispatch
	} = useIdentity();

	const { t } = useTranslation();
	const { search } = useLocation();

	const [isAddUserModalOpen, setIsAddUserModalOpen] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});
	const [currentPage, setCurrentPage] = useState(1);
	const [filterBy, setFilterBy] = useState<string>('email');
	const [selectedUserId, setSelectedUserId] = useState<string>('');
	const [query, setQuery] = useState<string>('');
	const [isUnblockLoading, setIsUnblockLoading] = useState<boolean>(false);
	const [userToUnblock, setUserToUnblock] = useState<IUserData | null>(null);
	const [isUnblockModalOpen, setIsUnblockModalOpen] = useState<boolean>(false);

	const filtersFromUrl: IUserFilters = parseSearchParams(search) || {};

	useEffect(() => {
		return () => {
			setInitialUserList(dispatch);
		};
	}, []);

	const onCloseModal = (): void => setModalState(prevState => ({ ...prevState, isOpen: false }));

	const successModal = (): void => {
		setIsAddUserModalOpen(false);
		setModalState({
			isOpen: true,
			modalTitle: 'Add user',
			content: (
				<div className="fs-6">
					User was successfully created!
				</div>
			),
			minHeight: 100,
			confirmButtonText: t('PARTNERS.SUCCESS_BTN_NAME'),
			onConfirm: async () => {
				onCloseModal();
				await fetchUsersList({
					dispatch,
					usersListPaginationTokens,
					currentPage,
					searchParams: filtersFromUrl
				});
			},
			onClose: onCloseModal
		});
	};

	const errorModal = (): void => {
		setIsAddUserModalOpen(false);
		setModalState({
			isOpen: true,
			modalTitle: 'Add user',
			content: (
				<div className="fs-6">
					Failed creating new user!
				</div>
			),
			minHeight: 100,
			confirmButtonText: t('PARTNERS.SUCCESS_BTN_NAME'),
			onConfirm: (): void => {
				onCloseModal();
				setIsAddUserModalOpen(true);
			},
			onClose: onCloseModal
		});
	};

	const successReInviteModal = (email: string): void => {
		setIsAddUserModalOpen(false);
		setModalState({
			isOpen: true,
			modalTitle: 'Reinvite user',
			content: (
				<>
					<FontAwesomeIcon icon={faCircleCheck} width={40} className="h-40px mb-10" />
					<div className="fs-5">Invitation was successfully send!</div>
					<span className="fw-bolder fs-5">{email}.</span>

				</>
			),
			minHeight: 150,
			confirmButtonText: t('PARTNERS.SUCCESS_BTN_NAME'),
			onConfirm: onCloseModal,
			onClose: onCloseModal
		});
	};

	const errorReInviteModal = (): void => {
		setIsAddUserModalOpen(false);
		setModalState({
			isOpen: true,
			modalTitle: 'Re invite user',
			content: (
				<>
					<FontAwesomeIcon icon={faTriangleExclamation} width={40} className="h-40px mb-10 text-danger" />
					<div className="fs-5">
						Failed to send invitation!
					</div>
				</>
			),
			minHeight: 100,
			confirmButtonText: t('PARTNERS.SUCCESS_BTN_NAME'),
			onConfirm: onCloseModal,
			onClose: onCloseModal
		});
	};

	const onReInviteClick = async (user: IUserData): Promise<void> => {
		setSelectedUserId(user?.id);
		await resendInvitationToApplicant({ dispatch, email: user?.email, onSuccess: successReInviteModal, onError: errorReInviteModal });
		setSelectedUserId('');

	};

	const successUnblockModal = (email: string): void => {
		setModalState({
			isOpen: true,
			modalTitle: 'Unblock user',
			content: (
				<>
					<FontAwesomeIcon icon={faCircleCheck} width={40} className="h-40px mb-10" />
					<div className="fs-5">User was successfully unblocked!</div>
					<span className="fw-bolder fs-5">{email}</span>
				</>
			),
			minHeight: 150,
			confirmButtonText: t('PARTNERS.SUCCESS_BTN_NAME'),
			onConfirm: onCloseModal,
			onClose: onCloseModal
		});
	};

	const errorUnblockModal = (): void => {
		setModalState({
			isOpen: true,
			modalTitle: 'Unblock user',
			content: (
				<>
					<FontAwesomeIcon icon={faTriangleExclamation} width={40} className="h-40px mb-10 text-danger" />
					<div className="fs-5">
						Failed to unblock user!
					</div>
				</>
			),
			minHeight: 100,
			confirmButtonText: t('PARTNERS.SUCCESS_BTN_NAME'),
			onConfirm: onCloseModal,
			onClose: onCloseModal
		});
	};

	const onUnblockClick = (user: IUserData): void => {
		setUserToUnblock(user);
		setIsUnblockModalOpen(true);
	};

	const handleConfirmUnblock = async (): Promise<void> => {
		if (!userToUnblock) return;

		setSelectedUserId(userToUnblock.id);
		setIsUnblockLoading(true);
		setIsUnblockModalOpen(false);

		const onSuccess = async (email: string): Promise<void> => {
			// Refresh the users list after successful unblock
			await fetchUsersList({
				dispatch,
				usersListPaginationTokens,
				currentPage,
				searchParams: filtersFromUrl
			});
			successUnblockModal(userToUnblock.email);
		};

		const onError = (): void => {
			errorUnblockModal();
		};

		try {
			await unblockUser(userToUnblock.email, onSuccess, onError);
		} finally {
			setIsUnblockLoading(false);
			setSelectedUserId('');
			setUserToUnblock(null);
		}
	};



	const rowActions = (rowData: any): JSX.Element => {
		const user = rowData?.original;
		const isUserBlocked = user?.isUserBlocked !== null && user?.isUserBlocked !== undefined;

		return (
			<div className="d-flex align-items-center justify-content-center">
				<Link
					to={`/users/${user?.id}/${USER_TABS.USER_INFO}?email=${user?.email}`}
					state={{ search }}
					className="text-muted fs-7 fw-bolder"
				>
					View
				</Link>
				{
					user?.status !== 'CONFIRMED'
						? (
						<div
							className={clsx(
								'text-muted fs-7 fw-bolder ms-5 action-log-border w-50px',
								isResendInvitationLoading ? 'cursor-not-allowed' : 'cursor-pointer'
							)}
							onClick={(): Promise<void> => !isResendInvitationLoading && onReInviteClick(user)}
						>
							{
								isResendInvitationLoading && selectedUserId === user?.id
									? <ButtonLoading additionalClass="ms-2 text-muted" />
									: 'Reinvite'
							}
						</div>
					) : <div className="w-50px ms-5"/>
				}
				{
					isUserBlocked && (
						<div
							className={clsx(
								'text-muted fs-7 fw-bolder ms-5 action-log-border w-50px',
								isUnblockLoading ? 'cursor-not-allowed' : 'cursor-pointer'
							)}
							onClick={(): void => !isUnblockLoading && onUnblockClick(user)}
						>
							{
								isUnblockLoading && selectedUserId === user?.id
									? <ButtonLoading additionalClass="ms-2 text-muted" />
									: 'Unblock'
							}
						</div>
					)
				}
			</div>
		);
	};

	const listener = async (searchParams: IUserFilters): Promise<void> => {

		let localSearchParams: IUserFilters = {};

		if(Object.keys(searchParams).length > 0) {
			localSearchParams = searchParams;

			setFilterBy(searchParams?.filterBy ?? 'email');
			setQuery(searchParams?.query ?? '');
		} else {
			localSearchParams = {
				filterBy,
				query: '',
			};
		}

		await fetchUsersList({
			dispatch,
			usersListPaginationTokens,
			currentPage,
			searchParams: localSearchParams
		});


	};

	return (
		<>
			<PageTitle breadcrumbs={[]}>{t('MENU.USERS')}</PageTitle>
			<div>
				<UserFilterSection
					filterBy={filterBy}
					setFilterBy={setFilterBy}
					setIsAddUserModalOpen={setIsAddUserModalOpen}
					query={query}
					setQuery={setQuery}
					currentPage={currentPage}
				/>

				<div className="card">
					<Table
						name={TablesNames.userListTableData}
						data={usersList ?? []}
						isLoading={isUserListLoading}
						enableRowActions={true}
						rowActions={rowActions}
						actionColumnsSize={125}
						queryParamsListener={listener}
					/>
					<div
						className={clsx('card-body', (isUserListLoading || usersList.length === 0 || usersListPaginationTokens.length === 0) && 'd-none')}>
						<SimplePagination
							currentPage={currentPage}
							setCurrentPage={setCurrentPage}
							hasNextPage={hasUserNextPage}
						/>
					</div>
				</div>

				<ModalWindow
					{...modalState}
					onClose={onCloseModal}
				/>
				<ModalWindow
					isOpen={isAddUserModalOpen}
					modalTitle='Add user'
					backdrop="static"
					content={
						<UserModalContent
							onCancel={(): void => setIsAddUserModalOpen(false)}
							onSuccess={successModal}
							onError={errorModal}
						/>
					}
					onClose={(): void => setIsAddUserModalOpen(false)}
				/>
				<ModalWindow
					isOpen={isUnblockModalOpen}
					modalTitle='Confirm Unblock User'
					modalText={`Are you sure you want to unblock this user? ${userToUnblock?.email}`}
					minHeight={150}
					confirmButtonText='Unblock User'
					cancelButtonText='Cancel'
					onConfirm={handleConfirmUnblock}
					onCancel={(): void => {
						setUserToUnblock(null);
						setIsUnblockModalOpen(false);
					}}
					onClose={(): void => {
						setUserToUnblock(null);
						setIsUnblockModalOpen(false);
					}}
				/>
			</div>
		</>
	);
};
